import type {
  PatientTaskPriority,
  PatientTaskStatus,
  PatientTaskType,
} from '@/features/patient-record/types';

import type { TaskFormValues } from './TaskFormFields.types';

export const TASK_TYPES: readonly PatientTaskType[] = [
  'GENERAL',
  'CONTACT_PATIENT',
  'SCHEDULE_APPOINTMENT',
  'ORDER_TEST',
  'REVIEW_RESULTS',
  'SCHEDULE_FOLLOWUP',
  'SEND_RESULTS_LETTER',
];

export const TASK_PRIORITIES: readonly PatientTaskPriority[] = [
  'LOW',
  'MEDIUM',
  'HIGH',
  'URGENT',
];

export const TASK_STATUSES: readonly PatientTaskStatus[] = ['TODO', 'DONE', 'CANCELLED'];

/** Severity rank, most-urgent first — the canonical task-priority ordering. */
export const PRIORITY_ORDER: Record<PatientTaskPriority, number> = {
  URGENT: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

/**
 * Comparator that puts the earliest due date first (tasks with no due date
 * last), breaking ties by descending priority. Shared by the per-patient
 * panel and the My Cases grouped view so both order tasks identically.
 */
export function compareByDueThenPriority(
  a: { readonly dueDate: string | null; readonly priority: PatientTaskPriority },
  b: { readonly dueDate: string | null; readonly priority: PatientTaskPriority },
): number {
  if (a.dueDate !== null && b.dueDate !== null) {
    const diff = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    if (diff !== 0) return diff;
  } else if (a.dueDate !== null) {
    return -1;
  } else if (b.dueDate !== null) {
    return 1;
  }
  return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
}

export const EMPTY_FORM_VALUES: TaskFormValues = {
  title: '',
  type: 'GENERAL',
  priority: 'MEDIUM',
  status: 'TODO',
  dueDate: '',
  description: '',
  notes: '',
  assignee: null,
};

/**
 * Colour-coded due-date legend (PRD §Tasks Chip): red = overdue,
 * amber = due within 7 days, neutral = otherwise.
 */
export function classifyDueDate(due: string | null): 'overdue' | 'soon' | 'normal' | 'none' {
  if (due === null) return 'none';
  const today = startOfToday();
  const dueMs = new Date(due).getTime();
  if (Number.isNaN(dueMs)) return 'none';
  if (dueMs < today.getTime()) return 'overdue';
  const sevenDaysMs = today.getTime() + 7 * 24 * 60 * 60 * 1000;
  if (dueMs <= sevenDaysMs) return 'soon';
  return 'normal';
}

function startOfToday(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}
